import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, KeyRound, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public page hit from the password-reset email. Reads `?token=…`,
 * validates it server-side via `/api/auth/reset-password/verify`, and
 * lets the user set a new password if the token is fresh.
 *
 *  States:
 *    checking  : showing spinner while /verify is in flight
 *    invalid   : token rejected (missing / used / expired)
 *    ready     : valid token — show the two password fields
 *    submitting: POST /reset-password in flight
 *    done      : success — show CTA to sign in
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [status, setStatus] = useState('checking');
  const [errMessage, setErrMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus('invalid');
      setErrMessage('No reset token found in the URL. Please use the link from your email.');
      return undefined;
    }
    axios
      .get(`${BACKEND_URL}/api/auth/reset-password/verify`, { params: { token } })
      .then((r) => {
        if (cancelled) return;
        if (r?.data?.valid) {
          setStatus('ready');
        } else {
          setStatus('invalid');
          setErrMessage(
            'This password reset link is invalid or has expired. Request a new one from the sign-in page.'
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('invalid');
        setErrMessage(
          'We could not verify your reset link. Please try requesting a new one.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match — please retype them.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setStatus('done');
      toast.success('Password reset successfully. Sign in with your new password.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || JSON.stringify(d)).join('; ')
            : null;
      toast.error(msg || 'Could not reset password. The link may have expired.');
      // If the token was rejected mid-flow, swap to invalid state so the
      // user gets the canonical CTA back to the sign-in page.
      if (err?.response?.status === 400) {
        setStatus('invalid');
        setErrMessage(
          msg || 'This password reset link is invalid or has expired. Please request a new one.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 leading-tight">
              Reset your password
            </h1>
            <p className="text-xs text-slate-500">Rupiwise secure account recovery</p>
          </div>
        </div>

        {status === 'checking' && (
          <div
            className="py-10 flex flex-col items-center gap-3 text-slate-500"
            data-testid="reset-password-checking"
          >
            <Loader2 className="w-7 h-7 animate-spin" />
            <p className="text-sm">Verifying your reset link…</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="space-y-4" data-testid="reset-password-invalid">
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">Link not valid</div>
                <p className="leading-relaxed">{errMessage}</p>
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate('/auth')}
              data-testid="reset-password-back-to-signin-btn"
            >
              Back to sign in
            </Button>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <p className="text-sm text-slate-600 leading-relaxed">
              Pick a strong password you don't use anywhere else. Minimum 6 characters.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                autoComplete="new-password"
                data-testid="reset-password-new-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="reset-password-confirm-input"
                className="h-11"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-rose-600">Passwords don't match.</p>
              )}
            </div>
            <Button
              type="submit"
              disabled={submitting || !newPassword || newPassword !== confirmPassword}
              className="w-full h-11"
              data-testid="reset-password-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Set new password'
              )}
            </Button>
          </form>
        )}

        {status === 'done' && (
          <div className="space-y-4" data-testid="reset-password-success">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">All set</div>
                <p className="leading-relaxed">
                  Your password has been updated. You can now sign in with the new one.
                </p>
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate('/auth')}
              data-testid="reset-password-go-to-signin-btn"
            >
              Sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
